/**
 * QueueDetailDrawer
 *
 * Overlay slide-out drawer from the right edge of the viewport.
 * Displays queue entry metadata, an overall evaluation score bar,
 * the agent activity timeline, and any evaluation flags.
 *
 * Used on:
 * - DocumentQueue (queue list page)
 * - DocumentReviewer (review page)
 * - PODetail (purchase order detail page)
 * - POList (purchase order list page)
 */

import { useState, useEffect, useCallback } from 'react';
import StatusBadge from '../common/StatusBadge';
import ExtractionPipelineTimeline from './ExtractionPipelineTimeline';
import { QualityIssues } from './ExtractionPipelineTimeline';
import ValidationResults from './ValidationResults';
import { formatDate } from '../../utils/formatDate';
import ConfidenceBar from '../common/ConfidenceBar';
import { getOverallScore, getUnifiedIssues } from '../../utils/confidenceUtils';
import { calculateDocumentCost, formatCost } from '../../utils/pricingUtils';
import { resolveQueueIssue } from '../../api/queueApi';

// Step IDs that are pipeline steps despite having category="validation"
const PIPELINE_STEP_IDS = new Set(['duplicate', 'master_lookup']);

/**
 * @param {Object} props
 * @param {boolean} props.open - Whether the drawer is visible
 * @param {Object} props.queueEntry - Queue entry object (status, source, createdAt, etc.)
 * @param {Object|null} [props.evaluation] - LLM-as-judge evaluation result
 * @param {Object|null} [props.confidence] - Legacy confidence scores (backward compat)
 * @param {string} [props.filename] - Display name override (falls back to queueEntry.originalFilename)
 * @param {Array} [props.processingLog] - Override for queueEntry.processingLog (e.g. real-time polling updates)
 * @param {Function} [props.onClose] - Called when the user closes the drawer
 * @param {boolean} [props.loading] - Show a loading spinner inside the drawer
 */
function QueueDetailDrawer({ open, queueEntry, evaluation, confidence, filename, processingLog, onClose, loading }) {
  const [tab, setTab] = useState('issues');
  // Tracks issue indices resolved in this session (for immediate UI feedback
  // before the parent re-fetches the queue entry from the server).
  const [resolvedIndices, setResolvedIndices] = useState(new Set());

  // Two-phase animation: `mounted` keeps the DOM alive during exit,
  // `active` drives the CSS transition classes.
  const [mounted, setMounted] = useState(false);
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      // Double rAF ensures the browser paints the initial (off-screen)
      // state before we flip the transition class.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setActive(true));
      });
    } else {
      setActive(false);
      const timer = setTimeout(() => setMounted(false), 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Reset local resolution state when the queue entry changes
  useEffect(() => {
    setResolvedIndices(new Set());
  }, [queueEntry?._id]);

  // Called after a suggested action executes successfully — persists the
  // resolution to the DB and updates local state so the issue disappears.
  const handleIssueResolved = useCallback(async (issueIndex) => {
    if (issueIndex == null || !queueEntry?._id) return;
    try {
      await resolveQueueIssue(queueEntry._id, issueIndex);
      setResolvedIndices(prev => new Set(prev).add(issueIndex));
    } catch (err) {
      // Action already succeeded; resolution is best-effort.
      // The issue will still disappear on next full refresh.
      console.error('Failed to mark issue resolved:', err);
    }
  }, [queueEntry?._id]);

  if (!mounted) return null;

  // Derived display values
  const displayName = filename || queueEntry?.originalFilename;
  const overall = queueEntry ? getOverallScore(evaluation, confidence) : null;
  // Merge server-side resolvedAt with locally-resolved indices
  const rawIssues = queueEntry ? getUnifiedIssues(queueEntry, evaluation) : [];
  const issues = rawIssues.map((issue, idx) =>
    resolvedIndices.has(idx) ? { ...issue, resolvedAt: issue.resolvedAt || 'local' } : issue
  );

  // Split processing log into pipeline vs validation steps
  const steps = processingLog || queueEntry?.processingLog;
  const pipelineSteps = steps?.filter(s => s.category !== 'validation' || PIPELINE_STEP_IDS.has(s.stepId)) || [];
  const validationSteps = steps?.filter(s => s.category === 'validation' && !PIPELINE_STEP_IDS.has(s.stepId)) || [];
  const warningOrErrorCount = validationSteps.filter(s => s.status === 'warning' || s.status === 'error').length;
  const hasSteps = steps && steps.length > 0;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop — subtle scrim, click to close */}
      <div
        className={`absolute inset-0 transition-opacity duration-200 ${
          active ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ backgroundColor: 'rgba(15, 23, 42, 0.12)' }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Slide-in panel */}
      <div
        className={`absolute inset-y-0 right-0 w-full max-w-md flex flex-col bg-white transform transition-transform duration-300 ${
          active ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          transitionTimingFunction: 'cubic-bezier(0.32, 0.72, 0, 1)',
          boxShadow: '-12px 0 48px -12px rgba(0, 0, 0, 0.12)',
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Document details"
      >
        {/* Blue accent bar at top */}
        <div className="h-0.5 bg-gradient-to-r from-blue-500 to-blue-600 flex-shrink-0" />

        {/* Header — fixed, does not scroll */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex-1 min-w-0 mr-3">
            <h2 className="text-sm font-semibold text-gray-900 truncate">
              {loading && !queueEntry ? 'Loading...' : (displayName || 'Document Details')}
            </h2>
            {queueEntry && (
              <div className="flex items-center gap-2 mt-1">
                <StatusBadge variant="queue" status={queueEntry.status} />
                {queueEntry.source && (
                  <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                    queueEntry.source === 'api' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {queueEntry.source === 'api' ? 'API' : 'Web'}
                  </span>
                )}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
            aria-label="Close drawer"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : !queueEntry ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <svg className="w-10 h-10 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
              <p className="text-sm">No document selected</p>
            </div>
          ) : (
            <>
              {/* Metadata */}
              <div className="space-y-3 text-sm mb-5">
                {(() => {
                  const ext = queueEntry.originalFilename?.split('.').pop();
                  if (!ext || ext === queueEntry.originalFilename) return null;
                  return (
                    <div className="flex justify-between">
                      <span className="text-gray-500">File Type</span>
                      <span className="px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-700 uppercase">
                        {ext}
                      </span>
                    </div>
                  );
                })()}
                <div className="flex justify-between">
                  <span className="text-gray-500">Uploaded</span>
                  <span className="text-gray-900">{formatDate(queueEntry.createdAt)}</span>
                </div>
                {queueEntry.extractedAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Extracted</span>
                    <span className="text-gray-900">{formatDate(queueEntry.extractedAt)}</span>
                  </div>
                )}
                {queueEntry.extractionProvider && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Provider</span>
                    <span className="capitalize text-gray-900">{queueEntry.extractionProvider}</span>
                  </div>
                )}
                {queueEntry.error && (
                  <div className="bg-red-50 p-2.5 rounded-md text-red-700 text-xs">
                    {queueEntry.error}
                  </div>
                )}
                {overall != null && <ConfidenceBar value={overall} />}
                {queueEntry.tokenUsage?.length > 0 && (() => {
                  const { totalCost, breakdown } = calculateDocumentCost(queueEntry.tokenUsage);
                  return (
                    <div className="flex justify-between items-center group relative">
                      <span className="text-gray-500">Processing Cost</span>
                      <span className="text-gray-900 font-mono text-xs cursor-help">
                        {formatCost(totalCost)}
                      </span>
                      <div className="absolute right-0 top-full mt-1 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 whitespace-nowrap shadow-lg">
                        {breakdown.map((b, i) => (
                          <div key={i} className="flex justify-between gap-4">
                            <span className="text-gray-300">{b.label} <span className="text-gray-500">({b.model})</span></span>
                            <span className="font-mono">{formatCost(b.cost)}</span>
                          </div>
                        ))}
                        <div className="border-t border-gray-700 mt-1 pt-1 flex justify-between gap-4 font-medium">
                          <span>Total</span>
                          <span className="font-mono">{formatCost(totalCost)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Tabbed section: Issues | Extraction | Validation */}
              {hasSteps && (
                <>
                  <div className="flex border-b border-gray-200 mb-4 -mx-5 px-5">
                    <button
                      onClick={() => setTab('issues')}
                      className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
                        tab === 'issues'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Issues
                      {issues.filter(i => !i.resolvedAt).length > 0 && (
                        <span className="ml-1.5 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-semibold leading-none rounded-full bg-amber-100 text-amber-700">
                          {issues.filter(i => !i.resolvedAt).length}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => setTab('extraction')}
                      className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
                        tab === 'extraction'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Extraction
                    </button>
                    {validationSteps.length > 0 && (
                      <button
                        onClick={() => setTab('validation')}
                        className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
                          tab === 'validation'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Validation
                        {warningOrErrorCount > 0 && (
                          <span className="ml-1.5 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-semibold leading-none rounded-full bg-amber-100 text-amber-700">
                            {warningOrErrorCount}
                          </span>
                        )}
                      </button>
                    )}
                  </div>

                  {tab === 'issues' && <QualityIssues issues={issues} onIssueResolved={handleIssueResolved} />}

                  {tab === 'extraction' && pipelineSteps.length > 0 && (
                    <ExtractionPipelineTimeline steps={pipelineSteps} />
                  )}

                  {tab === 'validation' && (
                    <ValidationResults steps={validationSteps} />
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default QueueDetailDrawer;
